import { Alert, Button, Stack, Typography } from "@mui/material";
import { useState } from "react";

/**
 * Test page to demonstrate the ErrorBoundary functionality.
 * This page intentionally throws errors to test error handling.
 */
export const TestErrorPage = () => {
  const [shouldThrow, setShouldThrow] = useState(false);

  // This will throw an error during render
  if (shouldThrow) {
    // Simulate various types of errors
    const obj = null;
    // @ts-expect-error - Intentionally accessing property of null to trigger error
    return obj.property.value; // This will throw "Cannot read property 'value' of null"
  }

  return (
    <Stack spacing={3} sx={{ p: 4, maxWidth: 600, mx: "auto" }}>
      <Typography variant="h4">Error Boundary Test Page</Typography>
      <Typography variant="body1" color="text.secondary">
        This page is for testing the error boundary. Click any button below to trigger an error and
        see how the error boundary catches and displays it.
      </Typography>

      <Alert severity="info">
        Note: Error boundaries only catch errors during rendering, in lifecycle methods, and in
        constructors. They do NOT catch errors in event handlers or async code.
      </Alert>

      <Stack spacing={2}>
        <Button variant="contained" color="error" onClick={() => setShouldThrow(true)}>
          Trigger Render Error (null pointer)
        </Button>

        <ThrowOnRender />

        <ThrowInLifecycle />

        <ThrowWithUndefinedFunction />
      </Stack>
    </Stack>
  );
};

// Component that throws immediately on render
const ThrowOnRender = () => {
  const [shouldError, setShouldError] = useState(false);

  if (shouldError) {
    throw new Error("Intentional error thrown during component render!");
  }

  return (
    <Button variant="contained" color="warning" onClick={() => setShouldError(true)}>
      Trigger Immediate Render Error
    </Button>
  );
};

// Component that throws during render with undefined function
const ThrowWithUndefinedFunction = () => {
  const [shouldError, setShouldError] = useState(false);

  if (shouldError) {
    // @ts-expect-error - Intentionally calling undefined function
    undefinedFunction();
  }

  return (
    <Button variant="contained" color="info" onClick={() => setShouldError(true)}>
      Trigger Undefined Function Error
    </Button>
  );
};

// Component that has a calculation that will error
const ThrowInLifecycle = () => {
  const [count, setCount] = useState(0);

  // This will cause a division by zero attempt converted to error
  const value =
    count > 0
      ? (() => {
          throw new Error("Calculated error during render!");
        })()
      : null;

  return (
    <Button variant="contained" color="secondary" onClick={() => setCount(1)}>
      Trigger Calculation Error
      {value}
    </Button>
  );
};
